import { useState } from 'react';
import { RATING_MAX, RATING_MIN, formatRating } from '@filmsloth/shared';
import type { MovieRating } from '../api/client.js';
import { useT } from '../i18n/index.js';

interface RatingStarsProps {
  rating: MovieRating | null;
  onRate: (score: number) => void;
  onClear: () => void;
}

/**
 * Our own score for a film: what you gave it, and what everyone gave it.
 *
 * Ten radio buttons rather than a slider or a star widget. A rating is a
 * discrete choice out of ten, a native radio group is what a screen reader
 * and a keyboard already understand, and there is nothing here a custom
 * control would do better.
 */
export function RatingStars({ rating, onRate, onClear }: RatingStarsProps) {
  const t = useT();
  const [hovered, setHovered] = useState<number | null>(null);
  const yours = rating?.yours ?? null;
  const shown = hovered ?? yours;

  const scores = Array.from(
    { length: RATING_MAX - RATING_MIN + 1 },
    (_, index) => RATING_MIN + index,
  );

  return (
    <div className="rating">
      <div
        className="rating__scale"
        role="radiogroup"
        aria-label={t('rating.label')}
        onPointerLeave={() => setHovered(null)}
      >
        {scores.map((score) => (
          <button
            key={score}
            type="button"
            role="radio"
            aria-checked={yours === score}
            aria-label={t('rating.score', { score, max: RATING_MAX })}
            className={`rating__step${shown !== null && score <= shown ? ' rating__step--on' : ''}`}
            onPointerEnter={() => setHovered(score)}
            onFocus={() => setHovered(score)}
            onBlur={() => setHovered(null)}
            onClick={() => onRate(score)}
          >
            {score}
          </button>
        ))}
      </div>

      <div className="rating__meta muted small">
        {rating && rating.count > 0 && rating.weighted !== null ? (
          <span>
            {t('rating.community', {
              score: formatRating(rating.weighted),
              count: rating.count,
            })}
            {/* Saying the score is still settling is more honest than hiding
                it until some arbitrary vote count. */}
            {rating.confident ? '' : ` · ${t('rating.provisional')}`}
          </span>
        ) : (
          <span>{t('rating.none')}</span>
        )}

        {yours !== null ? (
          <button type="button" className="btn btn--ghost btn--small" onClick={onClear}>
            {t('rating.clear')}
          </button>
        ) : null}
      </div>
    </div>
  );
}
